import type { IconType } from '@kognitos/lattice';
import {
  type V2BookConnectionCondition,
  type V2BookConnectionStatus,
  V2BookConnectionState,
  V2BookConnectionConditionStatus,
  V2BookConnectionConditionType,
  V2BookConnectionConditionReasonCode,
} from '@/generated/hooks/bdk/models';

/** Maps condition type enum values to their i18n translation keys. */
export const CONDITION_TYPE_I18N_KEYS: Record<string, string> = {
  BOOK_CONNECTION_CONDITION_TYPE_BOOK_VALID:
    'integrations.connectionCondition.type.bookValid',
  BOOK_CONNECTION_CONDITION_TYPE_AUTHENTICATION_VALID:
    'integrations.connectionCondition.type.authenticationValid',
  BOOK_CONNECTION_CONDITION_TYPE_CONFIGURATION_VALID:
    'integrations.connectionCondition.type.configurationValid',
  BOOK_CONNECTION_CONDITION_TYPE_DEPLOYMENT_READY:
    'integrations.connectionCondition.type.deploymentReady',
  BOOK_CONNECTION_CONDITION_TYPE_CREDENTIALS_VALID:
    'integrations.connectionCondition.type.credentialsValid',
  BOOK_CONNECTION_CONDITION_TYPE_DISCOVER_READY:
    'integrations.connectionCondition.type.discoverReady',
  BOOK_CONNECTION_CONDITION_TYPE_TRIGGER_INSTANCES_READY:
    'integrations.connectionCondition.type.triggerInstancesReady',
  BOOK_CONNECTION_CONDITION_TYPE_COMPATIBLE_RUNTIME_VERSION:
    'integrations.connectionCondition.type.compatibleRuntimeVersion',
  BOOK_CONNECTION_CONDITION_TYPE_POLL_INSTANCES_READY:
    'integrations.connectionCondition.type.pollInstancesReady',
};

/** Returns the list of conditions with a FALSE status (i.e. unmet/failed conditions). */
export function getFailedConditions(
  status?: V2BookConnectionStatus,
): V2BookConnectionCondition[] {
  return (
    status?.conditions?.filter(
      c =>
        c.status ===
        V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_FALSE,
    ) ?? []
  );
}

/**
 * Visual (icon + Tailwind color class) for a single condition status.
 * PENDING is rendered with a Spinner by the component, so its icon is unused
 * there; it is still provided for completeness.
 * Mirrors the icon/color convention in ConnectionStatusTag.
 */
export interface IConditionVisual {
  icon: IconType;
  className: string;
  isLoading?: boolean;
}

export function getConditionVisual(
  status?: V2BookConnectionConditionStatus,
): IConditionVisual {
  switch (status) {
    case V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_TRUE:
      return { icon: 'Check', className: 'text-success' };
    case V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_FALSE:
      return { icon: 'X', className: 'text-destructive' };
    case V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_PENDING:
      return { icon: 'Loader', className: 'text-warning', isLoading: true };
    case V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_UNKNOWN:
    default:
      // Not applicable / not run yet — shown as a muted dash ("skipped").
      return { icon: 'Minus', className: 'text-muted' };
  }
}

/**
 * Conditions that must pass before discovery is meaningful. If any of these is
 * FALSE, a DISCOVER_READY that is not yet TRUE is blocked, not "in progress".
 */
export const UPSTREAM_DISCOVERY_CONDITION_TYPES: V2BookConnectionConditionType[] =
  [
    V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_BOOK_VALID,
    V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_AUTHENTICATION_VALID,
    V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_CREDENTIALS_VALID,
    V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_CONFIGURATION_VALID,
    V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_DEPLOYMENT_READY,
  ];

/** True when any upstream (pre-discovery) condition has a FALSE status. */
export function hasBlockingUpstreamCondition(
  status?: V2BookConnectionStatus,
): boolean {
  return (
    status?.conditions?.some(
      c =>
        c.type !== undefined &&
        UPSTREAM_DISCOVERY_CONDITION_TYPES.includes(c.type) &&
        c.status ===
          V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_FALSE,
    ) ?? false
  );
}

/** True when DISCOVER_READY is PENDING with the IN_PROGRESS reason code. */
function isDiscoverReadyPending(status?: V2BookConnectionStatus): boolean {
  return (
    status?.conditions?.some(
      c =>
        c.type ===
          V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_DISCOVER_READY &&
        c.status ===
          V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_PENDING &&
        c.reason_code ===
          V2BookConnectionConditionReasonCode.BOOK_CONNECTION_CONDITION_REASON_CODE_DISCOVER_IN_PROGRESS,
    ) ?? false
  );
}

/**
 * Discovery is genuinely running only when it is in progress AND nothing
 * upstream is failing. This is the guard used to show "Discovering…".
 */
export function isDiscoveryActive(status?: V2BookConnectionStatus): boolean {
  return (
    isDiscoverReadyPending(status) && !hasBlockingUpstreamCondition(status)
  );
}

/**
 * The most recent time any check transitioned, used to show staleness
 * ("last scanned 2h ago"). Falls back to the status' last_updated.
 */
export function getLastScannedTime(
  status?: V2BookConnectionStatus,
): string | undefined {
  const transitions =
    status?.conditions
      ?.map(c => c.last_transition_time)
      .filter((t): t is string => !!t) ?? [];
  if (transitions.length > 0) {
    // ISO-8601 timestamps sort chronologically as strings, so we avoid
    // constructing Date objects (see the datetime-utils lint rule).
    return transitions.reduce((latest, current) =>
      current > latest ? current : latest,
    );
  }
  return status?.last_updated;
}

/**
 * Whether the user should be allowed to configure discovered entities.
 *
 * READY connections are always configurable. A failed/unmet connection is still
 * configurable when the failure is discovery-only (no upstream credentials/
 * config/deployment failure) — that's exactly where the user re-runs discovery
 * or cleans up a failed result. PENDING/UNKNOWN connections are not yet ready,
 * and INACTIVE ones must be reactivated before anything can be configured.
 */
export function canConfigureDiscoverables(
  status?: V2BookConnectionStatus,
): boolean {
  const state = status?.state;
  // A fully-ready connection is always configurable.
  if (state === V2BookConnectionState.BOOK_CONNECTION_STATE_READY) return true;
  // A failed/unmet connection is still configurable when the failure is
  // discovery-only (i.e. no upstream credentials/config/deployment failure) —
  // that's exactly where the user re-runs or cleans up discovery.
  if (
    state === V2BookConnectionState.BOOK_CONNECTION_STATE_FAILED ||
    state === V2BookConnectionState.BOOK_CONNECTION_STATE_CONDITIONS_NOT_MET
  ) {
    return !hasBlockingUpstreamCondition(status);
  }
  // PENDING / UNKNOWN / INACTIVE / unset: not ready to configure. An inactive
  // connection has no running pod to discover against, so it must be
  // reactivated first.
  return false;
}

/**
 * Delimiter shifu is told to place between the two answer sections
 * (`===FIXES===`). Matched leniently on a line of its own: the agent is an LLM
 * and drifts from the exact token (e.g. `=== FIXES`, `===fixes===`, `## Fixes`),
 * so we accept a "fixes" marker line decorated with any run of `=`/`#`/`*` on
 * either side (case-insensitive). Anchoring to a full line (`^…$`, multiline)
 * avoids matching the word "fixes" inside prose.
 */
const INSIGHT_SECTION_DELIMITER =
  /^[ \t]*[=#*]+[ \t]*fixes[ \t]*[=#*]*[ \t]*:?[ \t]*$/im;

/**
 * Splits shifu's answer into its two sections. Older/cached answers (and any
 * response that omits the delimiter) degrade gracefully: everything falls into
 * `cause` and `fixes` is empty.
 */
export function parseInsightSections(insight: string): {
  cause: string;
  fixes: string;
} {
  const [cause, ...rest] = insight.split(INSIGHT_SECTION_DELIMITER);
  return { cause: (cause ?? '').trim(), fixes: rest.join('').trim() };
}
